import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page } from 'react-pdf';
import {
  Check,
  ShieldCheck,
  RotateCw,
  CheckCircle2,
  XCircle,
  Clock,
  Ban,
  PenLine,
  Mail,
  X,
} from 'lucide-react';
import * as SigningService from '../service/signing';
import {
  toPixels,
  signerColor,
  maxLinesFor,
  formatSignedStamp,
  SIGNER_STAMP_FONT_SIZE,
} from '../smartsign/editor/sign-overlays';
import SmartSignLogo from '../assets/SmartSignLogo';
import SmartSignFooter from './SmartSignFooter';
import SignaturePadModal from './SignaturePadModal';
import OtpCodeInput from './OtpCodeInput';
import '../utils/pdfjsWorker';

/**
 * The public signing page (`/sign/:hash`) — Part 2 / Phase 9.
 *
 * Server-enforced flow (§11): email OTP → active consent → document with this
 * signer's fields → FRESH code at the Sign click. The session JWT lives in
 * memory only; a reload simply re-runs the OTP step.
 */
export default function SigningPage() {
  const { hash } = useParams();
  const [session, setSession] = useState(null);
  const [boot, setBoot] = useState(null); // GET /sign/s/:hash response
  const [loadFailed, setLoadFailed] = useState(false);
  const [values, setValues] = useState({});
  const [outcome, setOutcome] = useState(null); // 'signed_done' | 'signed_next' | 'declined_by_me'

  const load = useCallback(
    async (token) => {
      try {
        const res = await SigningService.getState(hash, token);
        if (res?.ok) setBoot(res);
        else setLoadFailed(true);
      } catch (err) {
        console.error('Signing page load failed', err);
        setLoadFailed(true);
      }
    },
    [hash],
  );

  useEffect(() => {
    load(null);
  }, [load]);

  if (loadFailed) {
    return (
      <Shell boot={boot}>
        <StatusCard
          icon={XCircle}
          tone='text-red-500'
          title='This signing link is not valid'
          text='The link may be incomplete or the request may have been removed. Check the email you received, or contact the sender.'
        />
      </Shell>
    );
  }
  if (!boot) {
    return (
      <Shell boot={boot}>
        <div className='flex items-center gap-2 text-sm text-gray-500 justify-center py-24'>
          <RotateCw size={15} className='animate-spin' /> Loading…
        </div>
      </Shell>
    );
  }

  if (outcome === 'declined_by_me') {
    return (
      <Shell boot={boot}>
        <StatusCard icon={Ban} tone='text-gray-500' title='You declined to sign' text='The sender has been updated. You can close this tab.' />
      </Shell>
    );
  }
  if (outcome) {
    return (
      <Shell boot={boot}>
        <StatusCard
          icon={CheckCircle2}
          tone='text-green-600'
          title='Signed'
          text={
            outcome === 'signed_done'
              ? 'Everyone has signed. A copy of the completed document is on its way to your inbox.'
              : 'Thank you — the next signer has been notified. You will receive the completed document by email once everyone has signed.'
          }
        />
      </Shell>
    );
  }

  const blocked = {
    completed: ['This document is already completed', 'Every signer has signed. Your copy was emailed to you.', CheckCircle2, 'text-green-600'],
    already_signed: ['You have already signed', 'You will receive the completed document by email once everyone has signed.', CheckCircle2, 'text-green-600'],
    declined: ['This request was declined', 'A signer declined, so the document can no longer be signed.', XCircle, 'text-red-500'],
    expired: ['This signing link has expired', 'Ask the sender to send a new request.', Clock, 'text-amber-500'],
    cancelled: ['This request was cancelled', 'The sender withdrew the document. Nothing further is needed from you.', Ban, 'text-gray-500'],
    not_your_turn: ['Not your turn yet', 'Signers complete this document in order — you will receive an email when it is your turn.', Clock, 'text-amber-500'],
  }[boot.state];
  if (blocked) {
    const [title, text, Icon, tone] = blocked;
    return (
      <Shell boot={boot}>
        <StatusCard icon={Icon} tone={tone} title={title} text={text} />
      </Shell>
    );
  }

  if (!boot.authenticated || !session) {
    const onAuthed = async (token) => {
      setSession(token);
      await load(token);
    };
    return (
      <Shell boot={boot}>
        {boot.requireOtp === false ? (
          <LinkOpener hash={hash} onOpened={onAuthed} />
        ) : (
          <OtpGate hash={hash} maskedEmail={boot.maskedEmail} onVerified={onAuthed} />
        )}
      </Shell>
    );
  }

  if (!boot.consented) {
    return (
      <Shell boot={boot}>
        <ConsentGate
          hash={hash}
          session={session}
          roleLabel={boot.roleLabel}
          onConsented={() => setBoot((prev) => ({ ...prev, consented: true }))}
        />
      </Shell>
    );
  }

  return (
    <SignScreen
      hash={hash}
      session={session}
      boot={boot}
      values={values}
      setValues={setValues}
      onOutcome={setOutcome}
      onDocumentSigned={(documentId) => {
        // Mark it signed locally rather than refetching: the signer stays in the
        // same session, and a full reload would drop the values they typed into
        // documents they have not submitted yet.
        setBoot((prev) => ({
          ...prev,
          documents: (prev.documents || []).map((d) =>
            d.id === documentId ? { ...d, signed: true } : d,
          ),
        }));
      }}
    />
  );
}

function Shell({ boot, children }) {
  return (
    <div className='flex min-h-screen flex-col bg-gray-100'>
      <NavBar boot={boot} />
      <div className='flex-1 px-3 py-6'>{children}</div>
      {/* The document screen mounts its own wrapper (see below) because of its
          fixed action bar, so the footer lands only on the terminal screens —
          which is where it belongs: behind a fixed bar it would be unreadable. */}
      <SmartSignFooter />
    </div>
  );
}

/**
 * The signing page's nav bar.
 *
 * Same content that used to sit centred above the card — the mark, the request
 * and the role — moved to the left of a white bar spanning the page.
 *
 * Sticky, because the page below it scrolls a full PDF: "which document, as
 * whom" is the thing a signer most needs while scrolling, and it was previously
 * the first thing to disappear.
 *
 * `boot` is null before the request loads and on an invalid link, so the bar
 * falls back to the product mark rather than vanishing — the chrome should not
 * appear and disappear between two screens of the same flow.
 */
function NavBar({ boot }) {
  return (
    <header className='sticky top-0 z-30 bg-white border-b border-gray-200'>
      <div className='flex items-center gap-3 px-4 py-2.5'>
        <SmartSignLogo size={32} />
        <div className='min-w-0'>
          {boot ? (
            <>
              {/*
                A fixed title. It used to read "<envelope name> — <item name>",
                which is a PDF filename defaulted from the first upload and a
                monday item name — both internal, neither chosen for an outside
                signer to read. "sample.pdf — Item 1" told the recipient nothing
                and leaked how the sender files their work.

                The document's own name is on the rail beside the page, where it
                identifies which of several is open. Up here it was decoration.
              */}
              <div className='text-sm font-semibold text-gray-900 leading-tight'>
                Signing Request
              </div>
              <div className='text-xs text-gray-500 truncate leading-tight'>
                For {boot.roleLabel} · via SmartSign
              </div>
            </>
          ) : (
            <>
              <div className='text-sm font-semibold text-gray-900 leading-tight'>SmartSign</div>
              <div className='text-xs text-gray-500 leading-tight'>Secure document signing</div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * The signing page's document rail.
 *
 * Since the action button advances by itself, this list is not how a signer MOVES
 * between documents — it is how they see where they are and what is left. Hence
 * the state in words ("signing now", "signed") rather than colour alone, which a
 * colour-blind signer or a printed screenshot cannot read.
 *
 * Clicking still works, for going back to re-read something already signed.
 *
 * Full width above the document on narrow screens, a sticky column beside it from
 * `md` up: at 320px a 208px column would leave nothing for the page it describes.
 */
function DocumentRail({ documents, activeId, onSelect }) {
  const signed = documents.filter((d) => d.signed).length;

  return (
    <aside className='w-full flex-shrink-0 md:w-52 md:sticky md:top-16'>
      <div className='bg-white border border-gray-200 rounded-lg p-2'>
        <div className='px-1.5 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400'>
          Documents
        </div>
        {documents.map((d, i) => {
          const active = d.id === activeId;
          return (
            <button
              key={d.id}
              onClick={() => onSelect(d.id)}
              className={`w-full flex items-start gap-2 text-left px-1.5 py-1.5 rounded-md transition-colors ${
                active ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <span
                className={`w-4 h-4 mt-0.5 rounded-full flex-shrink-0 inline-flex items-center justify-center text-[9px] font-semibold leading-none tabular-nums ${
                  d.signed
                    ? 'bg-green-600 text-white'
                    : active
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 text-gray-500'
                }`}
              >
                {d.signed ? <Check size={9} strokeWidth={4} /> : i + 1}
              </span>
              <span className='min-w-0'>
                <span
                  className={`block text-[11.5px] font-medium truncate ${
                    active ? 'text-blue-700' : d.signed ? 'text-gray-500' : 'text-gray-900'
                  }`}
                >
                  {d.name}
                </span>
                <span className='block text-[10px] text-gray-400'>
                  {d.pageCount} {d.pageCount === 1 ? 'page' : 'pages'}
                  {d.signed ? ' · signed' : active ? ' · signing now' : ''}
                </span>
              </span>
            </button>
          );
        })}
        <div className='px-1.5 pt-2 pb-0.5 text-[10px] text-gray-500'>
          {signed} of {documents.length} signed
        </div>
      </div>
    </aside>
  );
}

function StatusCard({ icon: Icon, tone, title, text }) {
  return (
    <div className='max-w-xl mx-auto bg-white border border-gray-200 rounded-xl px-6 py-12 text-center'>
      <Icon size={32} className={`mx-auto mb-3 ${tone}`} />
      <div className='text-base font-semibold text-gray-900'>{title}</div>
      <p className='text-sm text-gray-500 mt-1 max-w-sm mx-auto'>{text}</p>
    </div>
  );
}

/**
 * Entry when the sender turned identity verification off: the link IS the
 * credential, so there is nothing to ask for.
 *
 * Opens on arrival rather than behind a button. There WAS a button here, on the
 * reasoning that opening the session writes `auth.verified` to the audit trail
 * and that should record a moment the person meant. But clicking the link in
 * the email is that moment, and the consent gate lands immediately after — an
 * explicit, recorded choice, made before anything can be signed. A screen whose
 * only content is a button to see the next screen is a step, not a beat.
 */
function LinkOpener({ hash, onOpened }) {
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  // Held in a ref, and the effect depends on `hash` alone: `onOpened` is
  // rebuilt every render by the parent, so depending on it would re-run this —
  // and the cleanup would cancel the call already in flight.
  const onOpenedRef = useRef(onOpened);
  onOpenedRef.current = onOpened;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await SigningService.openSession(hash);
        if (cancelled) return;
        if (res?.ok && res.sessionToken) onOpenedRef.current(res.sessionToken);
        else setError('Could not open the document — try again.');
      } catch {
        if (!cancelled) setError('Could not open the document — try again.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hash, attempt]);

  if (error) {
    return (
      <div className='max-w-xl mx-auto bg-white border border-gray-200 rounded-xl px-6 py-10 text-center'>
        <XCircle size={28} className='mx-auto mb-3 text-red-500' />
        <div className='text-base font-semibold text-gray-900'>Could not open the document</div>
        <p className='text-sm text-gray-500 mt-1'>{error}</p>
        <button
          onClick={() => {
            setError(null);
            setAttempt((n) => n + 1);
          }}
          className='mt-5 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-2 text-sm text-gray-500 justify-center py-24'>
      <RotateCw size={15} className='animate-spin' /> Opening…
    </div>
  );
}

/**
 * Has this field been filled in?
 *
 * One definition, used by both the "N required left" gate and the progress
 * count, which previously carried the same four-line ladder twice — and a
 * checkbox added to one and not the other would have made the count and the
 * gate disagree about whether the document was done.
 *
 * An unticked checkbox is EMPTY, not false: that is what makes a required one
 * ("I accept the terms") block signing, and it is why `required` is the only
 * thing that turns it into a problem.
 */
function isEmptyValue(field, value) {
  if (field.type === 'signature') return !value.signatureDataUrl;
  if (field.type === 'date') return !value.date;
  if (field.type === 'text') return !(value.text || '').trim();
  if (field.type === 'checkbox') return value.checked !== true;
  return false;
}

/** Prove control of the invited inbox — the step `requireOtp` turns on. */
function OtpGate({ hash, maskedEmail, onVerified }) {
  const [stage, setStage] = useState('idle'); // idle | sending | sent
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const requestCode = async () => {
    setStage('sending');
    setError(null);
    try {
      const res = await SigningService.requestOtp(hash, 'verify');
      if (res?.ok) setStage('sent');
      else {
        setError(res?.error === 'too_many_attempts' ? 'Too many attempts — wait a minute and retry.' : 'Could not send the code — try again.');
        setStage('idle');
      }
    } catch {
      setError('Could not send the code — try again.');
      setStage('idle');
    }
  };

  const verify = async (codeArg) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await SigningService.verifyOtp(hash, (codeArg ?? code).trim());
      if (res?.ok && res.sessionToken) {
        onVerified(res.sessionToken);
      } else {
        // Clear the boxes so the retry starts clean, focus snapping back to box 1.
        setCode('');
        setError(
          {
            wrong_code: 'That code is not right — check the email and try again.',
            code_expired: 'That code expired — request a new one.',
            too_many_attempts: 'Too many wrong attempts — request a new code.',
            no_code_pending: 'Request a code first.',
          }[res?.error] || 'Verification failed — try again.',
        );
      }
    } catch {
      setCode('');
      setError('Verification failed — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='max-w-xl mx-auto bg-white border border-gray-200 rounded-xl px-6 py-10 text-center'>
      <ShieldCheck size={28} className='mx-auto mb-3 text-blue-600' />
      <div className='text-base font-semibold text-gray-900'>Verify it's you</div>
      <p className='text-sm text-gray-500 mt-1'>
        We'll email a one-time code to <span className='font-medium'>{maskedEmail}</span> to unlock the document.
      </p>

      {stage !== 'sent' ? (
        <button
          onClick={requestCode}
          disabled={stage === 'sending'}
          className='mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50'
        >
          {stage === 'sending' ? <RotateCw size={14} className='animate-spin' /> : <Mail size={14} />}
          Email me a code
        </button>
      ) : (
        <div className='mt-5 max-w-xs mx-auto'>
          {/* Entering the sixth digit verifies immediately; the button stays
              as the fallback (and the busy indicator). */}
          <OtpCodeInput
            value={code}
            onChange={(v) => {
              setCode(v);
              setError(null);
            }}
            onComplete={(v) => verify(v)}
            disabled={busy}
          />
          <button
            onClick={() => verify()}
            disabled={busy || code.length !== 6}
            className='mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50'
          >
            {busy && <RotateCw size={14} className='animate-spin' />}
            Continue
          </button>
          <button onClick={requestCode} className='mt-2 text-xs text-gray-500 hover:text-gray-700'>
            Send a new code
          </button>
        </div>
      )}

      {error && <p className='mt-3 text-xs text-red-600'>{error}</p>}
    </div>
  );
}

/** Step 2: the GDPR consent notice — an active choice, not a pre-ticked box. */
function ConsentGate({ hash, session, roleLabel, onConsented }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const agree = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await SigningService.giveConsent(hash, session);
      if (res?.ok) onConsented();
      else setError('Could not record your consent — try again.');
    } catch {
      setError('Could not record your consent — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='max-w-xl mx-auto bg-white border border-gray-200 rounded-xl px-6 py-8'>
      <div className='text-base font-semibold text-gray-900 mb-2'>Signatory Consent Notice</div>
      <div className='text-sm text-gray-600 space-y-2 leading-relaxed'>
        <p>
          You are about to review and electronically sign this document as{' '}
          <span className='font-medium'>{roleLabel}</span>. By continuing you agree that:
        </p>
        <ul className='list-disc pl-5 space-y-1'>
          <li>Your electronic signature is intended to be legally binding;</li>
          <li>
            Your name, email address, IP address and signing activity are recorded in an audit
            trail kept for compliance purposes.
          </li>
        </ul>
      </div>

      <label className='flex items-start gap-2 mt-4 cursor-pointer'>
        <input type='checkbox' checked={checked} onChange={(e) => setChecked(e.target.checked)} className='mt-0.5' />
        <span className='text-sm text-gray-800'>I have read and agree to the Signatory Consent Notice.</span>
      </label>

      <button
        onClick={agree}
        disabled={!checked || busy}
        className='mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50'
      >
        {busy && <RotateCw size={14} className='animate-spin' />}
        Agree & view document
      </button>
      {error && <p className='mt-2 text-xs text-red-600'>{error}</p>}
    </div>
  );
}

/** Step 3: the document with this signer's fields, then the confirm-code sign. */
function SignScreen({ hash, session, boot, values, setValues, onOutcome, onDocumentSigned }) {
  const [pageSizes, setPageSizes] = useState({});
  const [padFieldId, setPadFieldId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [issues, setIssues] = useState(null);

  const documents = boot.documents || [];
  // Start on the first document still unsigned: a reload mid-session resumes
  // where the signer left off rather than sending them back to page one.
  const [activeId, setActiveId] = useState(
    () => (documents.find((d) => !d.signed) || documents[0])?.id ?? null,
  );

  const activeDocument = documents.find((d) => d.id === activeId) || null;
  const fileUrl = useMemo(
    () => (activeId ? SigningService.getDocumentUrl(hash, activeId, session) : null),
    [hash, activeId, session],
  );
  // The rail is a real column from `md` up, and the page has to fit beside it —
  // sizing off the viewport alone overflowed the document into it.
  const viewport = typeof window !== 'undefined' ? window.innerWidth : 704;
  const RAIL_SPACE = 224; // w-52 plus the flex gap
  const railTakesSpace = documents.length > 1 && viewport >= 768;
  const pageWidth = Math.min(680, viewport - 24 - (railTakesSpace ? RAIL_SPACE : 0));

  // Page sizes belong to one document; a switch must not size boxes against the
  // previous document's pages.
  useEffect(() => {
    setPageSizes({});
    setIssues(null);
  }, [activeId]);

  const documentFields = activeDocument?.fields || [];
  const myFields = documentFields.filter((f) => f.signerIndex === boot.signerIndex);
  const myEditable = myFields.filter((f) => f.type !== 'date_signed');
  /**
   * Has this signer signed THIS document — the trigger for showing
   * `date_signed` boxes filled in.
   *
   * Every signature box, not the first one: a page with two of them is not
   * signed until both are, and a date that appeared after the first would read
   * as "done" over a document that still is not.
   *
   * The length check is not redundant. `every` on an empty list is true, so a
   * document carrying a date box and no signature box would otherwise show it
   * filled before the signer had touched anything.
   */
  const mySignatureFields = myFields.filter((f) => f.type === 'signature');
  const signedThisDocument =
    mySignatureFields.length > 0 &&
    mySignatureFields.every((f) => Boolean(values[f.id]?.signatureDataUrl));
  const requiredLeft = myEditable.filter(
    (f) => f.required && isEmptyValue(f, values[f.id] || {}),
  );
  const doneCount =
    myEditable.length - myEditable.filter((f) => isEmptyValue(f, values[f.id] || {})).length;

  /**
   * Sign the active document.
   *
   * Straight from the button — the "Apply your signature?" confirmation is gone.
   * Each click still commits ONE document server-side, which is what lets a
   * signer close the tab between documents without losing the ones already
   * signed, and what gives each document its own signature.applied audit event.
   */
  const submitDocument = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    setIssues(null);
    try {
      const res = await SigningService.complete(hash, activeId, values, session);
      if (res?.ok) {
        // More of this signer's documents to go: mark this one done and move on,
        // staying inside the same session rather than ending it.
        //
        // Advancing here is what makes the button honest. Marking it signed alone
        // left the signer on a document they had finished, with a second "Next
        // document" button to press — so a button labelled "Next" would have taken
        // two clicks to go next.
        if ((res.documentsRemaining ?? 0) > 0) {
          onDocumentSigned(activeId);
          const next = documents.find((d) => !d.signed && d.id !== activeId);
          if (next) setActiveId(next.id);
          return;
        }
        onOutcome(res.envelopeCompleted ? 'signed_done' : 'signed_next');
        return;
      }
      if (res?.issues?.length) {
        setIssues(res.issues);
        return;
      }
      setSubmitError(
        {
          consent_required: 'Consent is required before signing — reload the page.',
          session_required: 'Your session expired — reload the page and verify again.',
          document_already_signed: 'This document is already signed.',
        }[res?.error] || 'Signing failed — try again.',
      );
    } catch {
      setSubmitError('Signing failed — try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * What the primary button says: "Next" while this signer has documents left
   * after this one, "Finish" on the last — including when it is the only one.
   *
   * Derived from what is left to sign AFTER this document, not from this
   * document's position: the tabs let a signer take them out of order, and a
   * button reading "Next" with nothing left to go to would be a lie.
   */
  const unsignedElsewhere = documents.filter((d) => !d.signed && d.id !== activeId).length;
  const signLabel = unsignedElsewhere > 0 ? 'Next' : 'Finish';

  const setFieldValue = (fieldId, patch) =>
    setValues((prev) => ({ ...prev, [fieldId]: { ...prev[fieldId], ...patch } }));

  /**
   * Empty a signature field back to "Sign here".
   *
   * Deletes the key rather than nulling it, so a cleared field is byte-for-byte
   * an untouched one — both for the outstanding-field count here and for the
   * payload `complete` receives.
   */
  const clearSignature = (fieldId) =>
    setValues((prev) => {
      const entry = { ...(prev[fieldId] || {}) };
      delete entry.signatureDataUrl;
      const next = { ...prev };
      if (Object.keys(entry).length) next[fieldId] = entry;
      else delete next[fieldId];
      return next;
    });

  /**
   * The field's own label, floated just above its box.
   *
   * It was the input's `placeholder`, which meant it vanished the moment the
   * signer started typing — exactly when "what was this asking me for?" is
   * hardest to answer — and on a `date` or a `checkbox` it never showed at all,
   * because neither has a placeholder to show.
   *
   * Rendered OUTSIDE the box, so it cannot eat the room the answer needs: these
   * are ~19px tall at a date field and 13px at a checkbox. Above by default,
   * flipped below when the box sits near the top of the page, where the label
   * would be cut off by the paper's edge.
   *
   * It is a signing-page affordance ONLY and never reaches the PDF. Nothing here
   * is submitted, and the stamper resolves what it draws from `field.value`
   * alone — `PdfStampService` never reads a payload field's `label`.
   */
  const fieldLabel = (field, px, c) => {
    if (!field.label) return null;
    const above = px.top >= 13;
    return (
      <span
        key={`${field.id}-label`}
        aria-hidden
        className='absolute pointer-events-none truncate'
        style={{
          left: px.left,
          ...(above ? { top: px.top - 12 } : { top: px.top + px.height + 2 }),
          maxWidth: Math.max(px.width, 90),
          fontSize: 9,
          fontWeight: 600,
          lineHeight: 1.3,
          color: c.color,
        }}
      >
        {field.label}
      </span>
    );
  };

  /**
   * The required marker — an asterisk pinned to the box's top-right corner.
   *
   * Deliberately NOT part of `fieldLabel`, which returns null when a field has
   * no label. A required signature says "Sign here" and a required date says
   * nothing at all; both still have to be answerable before the button unlocks,
   * so the mark has to be independent of whether anyone captioned the field.
   *
   * Positioned OUTSIDE the box for the same reason the label is: these boxes go
   * down to ~13px tall, and anything drawn inside one competes with the answer.
   * It sits within the box's horizontal extent rather than off its right edge,
   * so a field placed hard against the page margin does not push the mark off
   * the paper — and clear of the signature's own remove badge, which is inside.
   *
   * Flips below on the same rule as the label: near the top of a page there is
   * no room above, and a mark cropped by the paper's edge marks nothing.
   *
   * Red — `#d83a52`, the same red the editor uses for a deleted column and a
   * missing signature — rather than the signer's own colour. Every other mark
   * on this page is tinted by WHOSE field it is, so a required marker in that
   * palette reads as more of the same rather than as a different kind of fact.
   * Red is the convention a signer already knows, and it is the one colour on
   * the page that is not saying "this box belongs to you".
   */
  const requiredMark = (field, px) => {
    if (!field.required) return null;
    const above = px.top >= 13;
    return (
      <span
        key={`${field.id}-req`}
        aria-hidden
        className='absolute pointer-events-none font-bold leading-none'
        style={{
          left: px.left + px.width - 5,
          ...(above ? { top: px.top - 9 } : { top: px.top + px.height + 1 }),
          fontSize: 12,
          color: '#d83a52',
        }}
      >
        *
      </span>
    );
  };

  const renderFieldControl = (field, size) => {
    const px = toPixels(field, size.width, size.height);
    const mine = field.signerIndex === boot.signerIndex;
    const c = signerColor(field.signerIndex);
    const v = values[field.id] || {};
    const base = {
      position: 'absolute',
      left: px.left,
      top: px.top,
      width: px.width,
      height: px.height,
    };

    if (!mine) {
      return (
        <div
          key={field.id}
          style={{ ...base, border: '1px dashed #CBD5E1', background: 'rgba(148,163,184,0.06)', borderRadius: 3 }}
          className='flex items-center justify-center overflow-hidden'
          title={`${boot.signers[field.signerIndex]?.roleLabel || 'Other signer'} completes this`}
        >
          <span className='text-[9px] text-gray-400 truncate px-1'>
            {boot.signers[field.signerIndex]?.roleLabel}
          </span>
        </div>
      );
    }

    if (field.type === 'signature') {
      const signed = Boolean(v.signatureDataUrl);
      // Always visible, never hover-revealed: signers open this page on phones
      // as often as not, and a hover-only control simply does not exist there.
      //
      // Only offered where it fits, though. A minimum-size placement (h 0.03)
      // renders around 24px tall, and a control that size would sit on top of
      // the signature it is meant to remove — those fields use the pad's
      // "Remove signature" instead, which is always available and is also the
      // keyboard path, since this badge is a span inside a button.
      const roomForBadge = signed && px.height >= 34 && px.width >= 60;

      return (
        <button
          key={field.id}
          onClick={() => setPadFieldId(field.id)}
          style={{ ...base, border: `2px dashed ${c.color}`, background: signed ? '#fff' : c.background, borderRadius: 4 }}
          className='flex items-center justify-center overflow-hidden'
          title={signed ? 'Click to sign again or remove' : 'Click to sign'}
        >
          {signed ? (
            <>
              <img src={v.signatureDataUrl} alt='Your signature' className='max-w-full max-h-full object-contain' />
              {roomForBadge && (
                /* span, not button — buttons cannot nest. Same pattern as the
                   editor palette's signer tabs. */
                <span
                  role='button'
                  aria-label='Remove this signature'
                  title='Remove this signature'
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSignature(field.id);
                  }}
                  className='absolute top-0.5 right-0.5 p-0.5 rounded bg-white/90 border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200'
                  style={{ lineHeight: 0 }}
                >
                  <X size={12} />
                </span>
              )}
            </>
          ) : (
            <span className='inline-flex items-center gap-1 font-medium' style={{ color: c.color, fontSize: Math.max(9, Math.min(12, px.height * 0.28)) }}>
              <PenLine size={Math.min(13, px.height * 0.35)} /> Sign here
            </span>
          )}
        </button>
      );
    }

    if (field.type === 'date_signed') {
      /*
        Once they have signed, the box stops asking and starts answering.

        A dashed placeholder that never fills looks like the one field they
        forgot, and there is nothing they can do about it — the server writes
        this one and refuses a client value outright. Showing the date the
        moment the signature lands says "handled" in the only way a signer
        actually reads: the box has something in it.

        The server stamps when the signer finishes their whole PACK rather than
        when this document was clicked, so this is a prediction — but a date-
        only stamp makes it a safe one. It is wrong only for a signer who signs
        just before midnight UTC and finishes after it.
      */
      if (signedThisDocument) {
        const stamp = formatSignedStamp(new Date());
        // Shrunk to fit rather than cut, because that is what the stamper does
        // to this box too, so a box too small to hold the date reads the same
        // on screen as it will on paper. A default placement (~73pt) holds the
        // date at full size with room to spare; only a hand-shrunk box shrinks.
        //
        // 0.58em per character, measured against this string rather than
        // assumed — it is all digits and hyphens. The 8 is the `px-1` either
        // side.
        const fontSize = Math.max(
          6,
          Math.min(
            SIGNER_STAMP_FONT_SIZE,
            px.height * 0.62,
            (px.width - 8) / (stamp.length * 0.58),
          ),
        );
        return (
          <div
            key={field.id}
            style={{ ...base, border: `1px solid ${c.color}`, background: '#fff', borderRadius: 3, fontSize }}
            className='flex items-center overflow-hidden px-1 text-gray-900'
            title='Filled in for you — stamped when you finish signing'
          >
            <span className='truncate'>{stamp}</span>
          </div>
        );
      }

      return (
        <div
          key={field.id}
          style={{ ...base, border: `1px dashed ${c.color}`, background: c.background, borderRadius: 3 }}
          className='flex items-center overflow-hidden px-1'
          title='Filled automatically when you sign'
        >
          <span className='text-[9px] truncate' style={{ color: c.color }}>Date signed (auto)</span>
        </div>
      );
    }

    if (field.type === 'checkbox') {
      const ticked = v.checked === true;
      // The whole box is the hit target, not a nested <input>: placements go
      // down to ~12px on screen and a native checkbox inside one is untappable
      // on a phone. The tick is drawn to fill whatever the owner sized.
      return (
        <button
          key={field.id}
          type='button'
          role='checkbox'
          aria-checked={ticked}
          aria-label={field.label || 'Tick to confirm'}
          onClick={() => setFieldValue(field.id, { checked: !ticked })}
          style={{
            ...base,
            border: `2px solid ${c.color}`,
            background: ticked ? c.background : '#fff',
            borderRadius: 3,
          }}
          className='flex items-center justify-center overflow-hidden'
          title={field.label || (ticked ? 'Tick to remove' : 'Tick to confirm')}
        >
          {ticked && (
            <Check
              strokeWidth={3.5}
              style={{
                width: Math.max(8, px.width * 0.8),
                height: Math.max(8, px.height * 0.8),
                color: c.color,
              }}
            />
          )}
        </button>
      );
    }

    if (field.type === 'date') {
      return (
        <input
          key={field.id}
          type='date'
          value={v.date || ''}
          onChange={(e) => setFieldValue(field.id, { date: e.target.value })}
          style={{ ...base, border: `1px solid ${c.color}`, borderRadius: 3, fontSize: Math.max(9, Math.min(12, px.height * 0.6)) }}
          className='bg-white px-1 focus:outline-none'
        />
      );
    }

    // Text: the box's height decides its line budget, exactly as the stamper
    // will wrap it (maxLinesFor at the fixed stamp size). One line stays a
    // plain input; a taller box becomes a wrapping textarea so what the
    // signer types flows the way the finished document will.
    const boxHeightPt = size.heightPt ? field.h * size.heightPt : 0;
    const lines = maxLinesFor(boxHeightPt, SIGNER_STAMP_FONT_SIZE);
    if (lines > 1) {
      // Render at the stamped size scaled to this page's zoom, so the wrap
      // points on screen match the wrap points on paper.
      const scale = size.heightPt ? size.height / size.heightPt : 1;
      return (
        <textarea
          key={field.id}
          value={v.text || ''}
          maxLength={500}
          placeholder='Type here'
          onChange={(e) => setFieldValue(field.id, { text: e.target.value })}
          style={{
            ...base,
            border: `1px solid ${c.color}`,
            borderRadius: 3,
            fontSize: Math.max(9, SIGNER_STAMP_FONT_SIZE * scale),
            lineHeight: 1.15,
            resize: 'none',
          }}
          className='bg-white px-1 focus:outline-none overflow-hidden'
        />
      );
    }

    return (
      <input
        key={field.id}
        type='text'
        value={v.text || ''}
        maxLength={500}
        placeholder='Type here'
        onChange={(e) => setFieldValue(field.id, { text: e.target.value })}
        style={{ ...base, border: `1px solid ${c.color}`, borderRadius: 3, fontSize: Math.max(9, Math.min(12, px.height * 0.6)) }}
        className='bg-white px-1 focus:outline-none'
      />
    );
  };

  const renderField = (field, size) => {
    const px = toPixels(field, size.width, size.height);
    // Only this signer's own inputs are captioned or marked. Another party's
    // box already carries their role as its caption, and `date_signed` is
    // filled by the server — neither is asking the reader for anything, so
    // neither gets a label or a required marker.
    const mine =
      field.signerIndex === boot.signerIndex && field.type !== 'date_signed';
    const c = signerColor(field.signerIndex);
    return (
      <React.Fragment key={field.id}>
        {mine && fieldLabel(field, px, c)}
        {mine && requiredMark(field, px)}
        {renderFieldControl(field, size)}
      </React.Fragment>
    );
  };

  return (
    <div className='min-h-screen bg-gray-100 pb-24'>
      {/* This screen has its own wrapper rather than Shell's, because of the
          fixed action footer — so it mounts the bar itself. */}
      <NavBar boot={boot} />
      {/* The bar replaced a block that carried the top padding with it. */}
      <div className='h-5' />

      {/* Rail beside the document, stacked above it on narrow screens. */}
      <div className='mx-auto flex max-w-5xl flex-col gap-4 px-3 md:flex-row md:items-start'>
        {documents.length > 1 && (
          <DocumentRail documents={documents} activeId={activeId} onSelect={setActiveId} />
        )}

        <div className='min-w-0 flex-1'>
          {issues?.length > 0 && (
            <div className='mb-3 bg-red-50 border border-red-100 text-red-700 text-xs rounded-lg px-3 py-2'>
              {issues.map((i, idx) => (
                <div key={idx}>{i.problem}</div>
              ))}
            </div>
          )}

          <div className='flex flex-col items-center gap-4'>
            <Document
              key={activeId}
              file={fileUrl}
              loading={
                <div className='flex items-center gap-2 text-sm text-gray-500 py-16'>
                  <RotateCw size={15} className='animate-spin' /> Loading document…
                </div>
              }
              error={<div className='text-sm text-gray-500 py-16'>The document could not be loaded — refresh to try again.</div>}
            >
              {Array.from({ length: activeDocument?.pageCount || 1 }, (_, i) => {
                const pageNumber = i + 1;
                const size = pageSizes[pageNumber];
                return (
                  <div key={pageNumber} className='relative bg-white shadow mb-4' style={{ width: pageWidth }}>
                    <Page
                      pageNumber={pageNumber}
                      width={pageWidth}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onRenderSuccess={({ width, height, originalHeight }) =>
                        // heightPt is the page's TRUE height in PDF points — what
                        // decides how many stamped lines a text box holds, so the
                        // page can show a textarea exactly where the document will
                        // wrap (same maxLinesFor the stamper uses).
                        setPageSizes((prev) => ({ ...prev, [pageNumber]: { width, height, heightPt: originalHeight } }))
                      }
                    />
                    {size &&
                      documentFields
                        .filter((f) => f.page === pageNumber)
                        .map((f) => renderField(f, size))}
                  </div>
                );
              })}
            </Document>
          </div>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className='fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 px-4 py-3'>
        <div className='max-w-xl mx-auto flex items-center gap-3'>
          <span className='text-xs text-gray-500 min-w-0'>
            {activeDocument?.signed ? (
              <span className='text-green-700 font-medium'>This document is signed</span>
            ) : (
              <>
                <span className='block'>
                  {doneCount}/{myEditable.length} fields · signing as{' '}
                  <span className='font-medium'>{boot.roleLabel}</span>
                </span>
                {/*
                  The confirmation dialog was the only place that said signing is
                  irreversible. Removing it removed the disclosure, so it lives
                  here instead — a standing line rather than a step to dismiss.
                */}
                <span className='block text-[11px] text-gray-400'>
                  Your signature is applied when you continue and cannot be undone.
                </span>
              </>
            )}
            {submitError && (
              <span className='block text-[11px] text-red-600 mt-0.5'>{submitError}</span>
            )}
          </span>
          <span className='flex-1' />
          <button onClick={() => setDeclineOpen(true)} className='px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-50 border border-gray-200'>
            Decline
          </button>
          {activeDocument?.signed ? (
            (() => {
              const next = documents.find((d) => !d.signed);
              return next ? (
                <button
                  onClick={() => setActiveId(next.id)}
                  className='px-4 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
                >
                  Next document
                </button>
              ) : null;
            })()
          ) : (
            <button
              onClick={submitDocument}
              disabled={requiredLeft.length > 0 || submitting}
              title={
                requiredLeft.length
                  ? 'Complete the highlighted fields first'
                  : 'Applies your signature to this document'
              }
              className='inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50'
            >
              {submitting && <RotateCw size={13} className='animate-spin' />}
              {signLabel}
            </button>
          )}
        </div>
      </div>

      {padFieldId && (
        <SignaturePadModal
          roleLabel={boot.roleLabel}
          hasExisting={Boolean(values[padFieldId]?.signatureDataUrl)}
          onRemove={() => {
            clearSignature(padFieldId);
            setPadFieldId(null);
          }}
          onClose={() => setPadFieldId(null)}
          onConfirm={(dataUrl) => {
            setFieldValue(padFieldId, { signatureDataUrl: dataUrl });
            setPadFieldId(null);
          }}
        />
      )}

      {declineOpen && (
        <DeclineModal
          hash={hash}
          session={session}
          onClose={() => setDeclineOpen(false)}
          onDeclined={() => onOutcome('declined_by_me')}
        />
      )}
    </div>
  );
}

/** The sole-control step: a FRESH code confirms the actual sign click. */
/**
 * Final confirmation before the signature is applied. Single-OTP flow
 * (product decision 2026-08-10): the session from the entry OTP authorises
 * signing — no second code. The modal remains as the explicit "I mean it"
 * act guarding against an accidental Sign click.
 */
function DeclineModal({ hash, session, onClose, onDeclined }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const doDecline = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await SigningService.decline(hash, reason.trim(), session);
      if (res?.ok) onDeclined();
      else setError('Could not decline — try again.');
    } catch {
      setError('Could not decline — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
      <div className='bg-white rounded-xl shadow-xl w-full max-w-sm px-5 py-6'>
        <div className='text-sm font-semibold text-gray-900'>Decline to sign?</div>
        <p className='text-xs text-gray-500 mt-1'>
          This ends the request for every signer. The sender will see that you declined.
        </p>
        <textarea
          rows={3}
          value={reason}
          maxLength={1000}
          onChange={(e) => setReason(e.target.value)}
          placeholder='Reason (optional, shared with the sender)'
          className='mt-3 w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none resize-y'
        />
        {error && <p className='mt-1 text-xs text-red-600'>{error}</p>}
        <div className='flex gap-2 mt-3'>
          <button onClick={onClose} className='flex-1 px-3 py-2 rounded-md text-sm text-gray-600 border border-gray-200 hover:bg-gray-50'>
            Keep signing
          </button>
          <button
            onClick={doDecline}
            disabled={busy}
            className='flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50'
          >
            {busy && <RotateCw size={13} className='animate-spin' />}
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}
